import { Action } from 'redux';
import { DrugItem, DrugItemConstructor } from 'src/app/shared/interfaces/drug-item.interface';
import { DOSAGE_FREQUENCIES_ENUM } from 'src/app/shared/enums/dosage-frequency.enum';
import { IndividualDrugItem } from '../../../../shared/interfaces/drug-autocomplete-item.interface';

export const MEDICATION_STATE_REDUX_ID = 'MedicationInformation';

export interface SelectedMedication {
  drug: DrugItem;
  useGenericVersion: boolean;
  selectedDosage: string;
  selectedPackage: string;
  frequency: DOSAGE_FREQUENCIES_ENUM;
  dosageQuantity: number;
}

interface MedicationForm {
  selectedMedications: SelectedMedication[];
}

export interface GenericAndBrandObject {
  generic: DrugItem;
  brand: DrugItem;
}

export interface MedicationStateStructure {
  medicationForm: MedicationForm;
  brandToGenericMapping: GenericAndBrandObject[];
  isMedicationFormValid: boolean; /** Helps the DRX service know when appropriate to retrieve session id */
  currentEncodedMeds: string;
  individualMedications: IndividualDrugItem[];
}

export const INITIAL_MEDICATION_STATE: MedicationStateStructure = {
  medicationForm: {
    selectedMedications: []
  },
  brandToGenericMapping: [],
  isMedicationFormValid: false,
  currentEncodedMeds: null,
  individualMedications: []
};

export class SelectedMedicationsAction implements Action {
  type: string;
  form?: MedicationForm;
  brandToGenericMapping?: GenericAndBrandObject;
  drug?: DrugItem;
  drugId?: number;
  individualMedications?: IndividualDrugItem[];
  selectedMedications?: SelectedMedication[];
  medState?: MedicationStateStructure;
}

/** Action Types */
const ADD_MEDICATION_TO_SWAP_MAP = 'ADD_MEDICATION_TO_SWAP_MAP';
const SWAP_GENERIC_AND_BRAND = 'SWAP_GENERIC_AND_BRAND';
const REMOVE_DRUG_BY_ID = 'REMOVE_DRUG_BY_ID';
const SELECTED_MEDS_ARE_VALID = 'SELECTED_MEDS_ARE_VALID';
const SELECTED_MEDS_ARE_INVALID = 'SELECTED_MEDS_ARE_INVALID';
const STORE_CURRENT_MEDICATIONS = 'STORE_CURRENT_MEDICATIONS';
const SET_INDIVIDUAL_MEDICATIONS = 'SET_INDIVIDUAL_MEDICATIONS';
const SET_SELECTED_MEDICATIONS = 'SET_SELECTED_MEDICATIONS';
const SET_MEDICATION_STATE = 'SET_MEDICATION_STATE';

/** Actions */

/** Triggers an update to the mapping for Generic/Brand swap */
export function addMedicationToSwapMap(brandToGenericMapping: GenericAndBrandObject):SelectedMedicationsAction { return { type: ADD_MEDICATION_TO_SWAP_MAP, brandToGenericMapping }; }
export function swapGenericAndBrand(drug: DrugItem):SelectedMedicationsAction { return { type: SWAP_GENERIC_AND_BRAND, drug }; }
export function removeDrugById(drugId: number):SelectedMedicationsAction { return { type: REMOVE_DRUG_BY_ID, drugId }; }
export function selectedMedsAreValid():SelectedMedicationsAction { return { type: SELECTED_MEDS_ARE_VALID }; }
export function selectedMedsAreInvalid():SelectedMedicationsAction { return { type: SELECTED_MEDS_ARE_INVALID }; }
export function storeCurrentMedications():SelectedMedicationsAction { return { type: STORE_CURRENT_MEDICATIONS }; }
export function setIndividualMedications(individualMedications: IndividualDrugItem[]):SelectedMedicationsAction { return { type: SET_INDIVIDUAL_MEDICATIONS, individualMedications }; }
export function setSelectedMedications(selectedMedications: SelectedMedication[]):SelectedMedicationsAction { return { type: SET_SELECTED_MEDICATIONS, selectedMedications }; }
export function setMedicationState(medState: MedicationStateStructure):SelectedMedicationsAction { return { type: SET_MEDICATION_STATE, medState }; }

export function MedicationInformation( state: MedicationStateStructure = INITIAL_MEDICATION_STATE, action: SelectedMedicationsAction ) {

  const copiedState = JSON.parse(JSON.stringify(Object.assign({}, state)));

  switch (action.type) {

    case ADD_MEDICATION_TO_SWAP_MAP:

      const alreadyMappedBrand: boolean
        = copiedState.brandToGenericMapping.filter( (mappedObj: GenericAndBrandObject) => {
          return (
            mappedObj.brand.DrugID === action.brandToGenericMapping.brand.DrugID ||
            mappedObj.generic.DrugID === action.brandToGenericMapping.generic.DrugID
          );
        }).length > 0;

      if (!alreadyMappedBrand) {
        copiedState.brandToGenericMapping.push(action.brandToGenericMapping);
      }

      return Object.assign({}, state, copiedState);

    case REMOVE_DRUG_BY_ID: {

      const multipleDrugsOfTheSameId: boolean
        = copiedState.medicationForm.selectedMedications.filter( (selectedMed: SelectedMedication) => {
          return selectedMed.drug.DrugID === action.drugId;
        }).length > 1;

      if (!multipleDrugsOfTheSameId) {
        /** Remove the generic mapping table entry */
        copiedState.brandToGenericMapping
          = copiedState.brandToGenericMapping.filter( (mapObj: GenericAndBrandObject) => {
            return (
              action.drugId !== mapObj.brand.DrugID &&
              action.drugId !== mapObj.generic.DrugID
            );
          });
      }

      return Object.assign({}, state, copiedState);
    }

    case SWAP_GENERIC_AND_BRAND: {

      let willBeGeneric = false;

      /** Get the 'map' object which contains either the appropriate brand or generic value */
      const mappedMapObj
        = state.brandToGenericMapping.filter( (bgObj: GenericAndBrandObject) => {

          /** If the passed Id matches a brand or generic drug.... */
          if (bgObj.brand.DrugID === action.drug.DrugID || bgObj.generic.DrugID === action.drug.DrugID) {

            if (bgObj.brand.DrugID === action.drug.DrugID) {
              /** If the passed Id matches a brand drug... */
              willBeGeneric = true;
            } else {
              /** If the passed Id matches a generic drug... */
              willBeGeneric = false;
            }

            return bgObj;
          }

          return false;
        })[0];

      /** newDrug will become either the brand or the generic */
      const newDrug = willBeGeneric ? mappedMapObj.generic : mappedMapObj.brand;

      const newDrugConstructed = new DrugItemConstructor(
        newDrug.DrugID || null,
        newDrug.DrugName || null,
        newDrug.DrugType || null,
        newDrug.DrugTypeID || null,
        newDrug.ChemicalName || null,
        newDrug.GenericDrugID || null,
        newDrug.GenericDrugName || null,
        newDrug.DrugTypeNDA || null,
        newDrug.Dosages || null,
      );

      const newDrugFormGroupValues = {
        drug: newDrugConstructed,
        useGenericVersion: willBeGeneric,
        selectedDosage: null,
        selectedPackage: null,
        frequency: null,
        dosageQuantity: null
      };

      for (const copiedStateMed of copiedState.medicationForm.selectedMedications) {
        if (copiedStateMed.drug.DrugID === action.drug.DrugID) {
          Object.assign(copiedStateMed, newDrugFormGroupValues);
          break;
        }
      }

      return Object.assign({}, state, copiedState);
    }

    case SELECTED_MEDS_ARE_VALID: {
      copiedState.isMedicationFormValid = true;
      return Object.assign({}, state, copiedState);
    }

    case SELECTED_MEDS_ARE_INVALID: {
      copiedState.isMedicationFormValid = false;
      return Object.assign({}, state, copiedState);
    }


    case STORE_CURRENT_MEDICATIONS: {
      if (!state.medicationForm.selectedMedications || state.medicationForm.selectedMedications.length === 0) {
        copiedState.currentEncodedMeds = INITIAL_MEDICATION_STATE.currentEncodedMeds;
      } else {
        copiedState.currentEncodedMeds = window.btoa(JSON.stringify(state.medicationForm.selectedMedications));
      }
      return Object.assign({}, state, copiedState);
    }

    case SET_INDIVIDUAL_MEDICATIONS: {
      copiedState.individualMedications = action.individualMedications;
      return Object.assign({}, state, copiedState);
    }

    case SET_SELECTED_MEDICATIONS: {
      copiedState.medicationForm.selectedMedications = action.selectedMedications;
      return Object.assign({}, state, copiedState);
    }

    case SET_MEDICATION_STATE: {
      return Object.assign({}, state, action.medState);
    }

    default:
      return state;
  }

}

/** Unused but works */
function sortSelectedDrugs(selectedMedications: SelectedMedication[]) {
  const copy = Object.assign([], selectedMedications);

  function compare(a: SelectedMedication, b: SelectedMedication) {
    // Use toUpperCase() to ignore character casing
    const med1 = a.drug.DrugName.toUpperCase();
    const med2 = b.drug.DrugName.toUpperCase();

    let comparison = 0;
    if (med1 > med2) {
      comparison = 1;
    } else if (med1 < med2) {
      comparison = -1;
    }
    return comparison;
  }
  return copy.sort(compare);
}
